import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';

import { Button, Modal } from '@components/ui';
import { MATERIAL_CATEGORIES, UNIT_TYPES } from '@mocks/malzemeler';

export default function MaterialModal({ isOpen, onClose, onSubmit, material }) {
    const [formData, setFormData] = useState({
        name: '',
        category: 'structural',
        unitType: 'adet',
        unitPrice: '',
        estimatedQuantity: '',
        notes: '',
    });

    useEffect(() => {
        if (material) {
            setFormData({
                name: material.name,
                category: material.category,
                unitType: material.unitType,
                unitPrice: material.unitPrice.toString(),
                estimatedQuantity: material.estimatedQuantity.toString(),
                notes: material.notes || '',
            });
        } else {
            setFormData({
                name: '',
                category: 'structural',
                unitType: 'adet',
                unitPrice: '',
                estimatedQuantity: '',
                notes: '',
            });
        }
    }, [material, isOpen]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            ...formData,
            unitPrice: parseFloat(formData.unitPrice) || 0,
            estimatedQuantity: parseFloat(formData.estimatedQuantity) || 0,
        });
        onClose();
    };

    const estimatedCost = (parseFloat(formData.unitPrice) || 0) * (parseFloat(formData.estimatedQuantity) || 0);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            title={material ? '✏️ Malzeme Düzenle' : '📦 Yeni Malzeme'}
            footer={
                <>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onClose}
                        className="w-full sm:w-auto min-h-[48px]"
                    >
                        İptal
                    </Button>
                    <Button
                        type="submit"
                        form="material-form"
                        variant="primary"
                        icon={<Save size={18} />}
                        className="w-full sm:w-auto min-h-[48px]"
                    >
                        {material ? 'Güncelle' : 'Kaydet'}
                    </Button>
                </>
            }
        >
            <form id="material-form" onSubmit={handleSubmit} className="space-y-4">
                {/* Malzeme Adı */}
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                        Malzeme Adı *
                    </label>
                    <input
                        type="text"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-base"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Örn: Demir, Beton, PVC Pencere"
                    />
                </div>

                {/* Kategori ve Birim - Yan yana mobilde alt alta */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Kategori *
                        </label>
                        <select
                            required
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-base min-h-[48px]"
                            value={formData.category}
                            onChange={(e) => handleChange('category', e.target.value)}
                        >
                            {Object.entries(MATERIAL_CATEGORIES).map(([key, cat]) => (
                                <option key={key} value={key}>{cat.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Birim Tipi *
                        </label>
                        <select
                            required
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-base min-h-[48px]"
                            value={formData.unitType}
                            onChange={(e) => handleChange('unitType', e.target.value)}
                        >
                            {Object.entries(UNIT_TYPES).map(([key, label]) => (
                                <option key={key} value={key}>{label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Fiyat ve Miktar */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Birim Fiyat (₺) *
                        </label>
                        <input
                            type="number"
                            required
                            min="0"
                            step="0.01"
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-base"
                            value={formData.unitPrice}
                            onChange={(e) => handleChange('unitPrice', e.target.value)}
                            placeholder="0"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Tahmini Miktar ({UNIT_TYPES[formData.unitType]}) *
                        </label>
                        <input
                            type="number"
                            required
                            min="0"
                            step="0.01"
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-base"
                            value={formData.estimatedQuantity}
                            onChange={(e) => handleChange('estimatedQuantity', e.target.value)}
                            placeholder="0"
                        />
                    </div>
                </div>

                {/* Tahmini Maliyet Gösterimi */}
                <div className="bg-bg-tertiary rounded-lg p-4">
                    <div className="flex justify-between items-center">
                        <span className="text-text-secondary font-medium">Tahmini Maliyet</span>
                        <span className="text-xl sm:text-2xl font-bold text-primary">
                            {formatCurrency(estimatedCost)}
                        </span>
                    </div>
                    <p className="text-xs text-text-muted mt-1">
                        {formatCurrency(parseFloat(formData.unitPrice) || 0)} × {(parseFloat(formData.estimatedQuantity) || 0).toLocaleString('tr-TR')} {UNIT_TYPES[formData.unitType]}
                    </p>
                </div>

                {/* Not */}
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                        Notlar
                    </label>
                    <textarea
                        className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-base resize-none"
                        rows={3}
                        value={formData.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        placeholder="Malzeme hakkında ek notlar..."
                    />
                </div>
            </form>
        </Modal>
    );
}

